import { BUNDLED_VERSION_MAP } from './bundled-version-map';
import type { ModuleVersionItem, ModuleVersionMap, ModuleVersionOverride, ResolvedRemote } from './types';

export const SUPPORTED_VERSION_MAP_PROTOCOL = 1;

export class ModuleVersionMapError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ModuleVersionMapError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

function parseVersionItem(name: string, value: unknown, strict: boolean): ModuleVersionItem | undefined {
  if (!isRecord(value) || typeof value.current !== 'string') {
    if (strict) throw new ModuleVersionMapError(`模块 ${name} 缺少 current`);
    return undefined;
  }

  return {
    current: value.current,
    previous: typeof value.previous === 'string' ? value.previous : undefined,
    childrenUrl: typeof value.childrenUrl === 'string' ? value.childrenUrl : undefined,
    title: typeof value.title === 'string' ? value.title : undefined,
    desc: typeof value.desc === 'string' ? value.desc : undefined,
  };
}

function parseModuleItems(input: Record<string, unknown>, strict: boolean): ModuleVersionMap['modules'] {
  const modules: ModuleVersionMap['modules'] = {};
  for (const [name, value] of Object.entries(input)) {
    const item = parseVersionItem(name, value, strict);
    if (item) modules[name] = item;
  }
  return modules;
}

function parseTenantIDs(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is string => typeof item === 'string');
}

function parseOverride(override: unknown): ModuleVersionOverride | undefined {
  if (!isRecord(override) || !isRecord(override.match) || !isRecord(override.modules)) return undefined;
  return {
    match: {
      tenantID: parseTenantIDs(override.match.tenantID),
    },
    modules: parseModuleItems(override.modules, false),
  };
}

function parseOverrides(input: unknown): ModuleVersionMap['overrides'] {
  if (!Array.isArray(input)) return undefined;
  const overrides = input.map(parseOverride).filter(isDefined);
  return overrides.length ? overrides : undefined;
}

export function parseModuleVersionMap(input: unknown): ModuleVersionMap {
  if (!isRecord(input)) throw new ModuleVersionMapError('版本映射必须是对象');
  if (input.version !== SUPPORTED_VERSION_MAP_PROTOCOL) {
    throw new ModuleVersionMapError(`不支持的版本映射协议: ${String(input.version)}`);
  }
  if (typeof input.updatedAt !== 'string') throw new ModuleVersionMapError('版本映射缺少 updatedAt');
  if (!isRecord(input.modules)) throw new ModuleVersionMapError('版本映射缺少 modules');

  return {
    version: SUPPORTED_VERSION_MAP_PROTOCOL,
    updatedAt: input.updatedAt,
    modules: parseModuleItems(input.modules, true),
    overrides: parseOverrides(input.overrides),
  };
}

export function applyTenantOverrides(map: ModuleVersionMap, tenantID?: string): ModuleVersionMap {
  if (!tenantID || !map.overrides?.length) return map;
  const modules = { ...map.modules };
  for (const override of map.overrides) {
    if (!override.match.tenantID?.includes(tenantID)) continue;
    Object.assign(modules, override.modules);
  }
  return { ...map, modules };
}

export function resolveRemotes(map: ModuleVersionMap): ResolvedRemote[] {
  return Object.entries(map.modules)
    .filter(([, item]) => !item.current.startsWith('local:'))
    .map(([name, item]) => ({ name, entry: item.current }));
}

export function getModuleVersion(map: ModuleVersionMap, moduleName: string): string {
  const current = map.modules[moduleName]?.current;
  if (!current) return 'unknown';
  const queryVersion = new URL(current, window.location.href).searchParams.get('version');
  if (queryVersion) return queryVersion;
  const manifestVersion = current.match(/\/([^/]+)\/mf-manifest\.json(?:\?.*)?$/)?.[1];
  if (manifestVersion) return manifestVersion;
  const [, version] = current.split('@');
  return version ?? current;
}

/** 只比较模块入口指针，版本表描述或更新时间变化不会误触发业务模块更新。 */
export function getChangedModuleNames(current: ModuleVersionMap, next: ModuleVersionMap): string[] {
  const names = new Set([...Object.keys(current.modules), ...Object.keys(next.modules)]);
  return [...names]
    .filter(name => current.modules[name]?.current !== next.modules[name]?.current)
    .sort();
}

export interface FetchModuleVersionMapOptions {
  tenantID?: string;
  timeoutMs?: number;
  fetcher?: typeof fetch;
  fallbackOnError?: boolean;
}

export async function fetchModuleVersionMap(
  url: string | undefined,
  options: FetchModuleVersionMapOptions = {},
): Promise<ModuleVersionMap> {
  if (!url) return applyTenantOverrides(BUNDLED_VERSION_MAP, options.tenantID);

  const timeoutMs = options.timeoutMs ?? 3000;
  const fetcher = options.fetcher ?? fetch;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // 版本表是可变的发布指针，浏览器 HTTP 缓存和 Service Worker 都不能复用旧响应。
    const response = await fetcher(url, { cache: 'no-store', signal: controller.signal });
    if (!response.ok) throw new ModuleVersionMapError(`版本映射拉取失败: ${response.status}`);
    const parsed = parseModuleVersionMap(await response.json());
    return applyTenantOverrides(parsed, options.tenantID);
  } catch (error) {
    if (options.fallbackOnError === false) throw error;
    return applyTenantOverrides(BUNDLED_VERSION_MAP, options.tenantID);
  } finally {
    clearTimeout(timer);
  }
}
